import type { api } from '@udm/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { canAny } from '../../api/client';
import { deleteRule, fetchGroups, fetchMetricCatalog, fetchRules, saveRule } from '../../api/ops';
import { getDataSource } from '../../data';
import { Card, Field, Modal } from '../../ui/components';
import { formatDateTime } from '../../ui/format';
import { useMe } from '../auth/session';
import { useReportScope } from '../reports/ReportScope';
import { levelText } from './GroupsPage';

/**
 * Automation rules (AFM b, l): a measurement from a sensor of any manufacturer, a smart platform (through the
 * integration API) or a controller gives a working group a light level for a while.
 */
export function RulesTab() {
  const { t } = useTranslation();
  const me = useMe().data;
  const { clientId } = useReportScope();
  const rules = useQuery({
    queryKey: ['automation-rules', clientId],
    queryFn: () => fetchRules(clientId),
    enabled: !!clientId,
  });
  const groups = useQuery({
    queryKey: ['groups', clientId],
    queryFn: () => fetchGroups(clientId),
    enabled: !!clientId,
  });
  const [editing, setEditing] = useState<api.AutomationRule | 'new' | null>(null);
  const gname = (id: string | null) => groups.data?.items.find((g) => g.id === id)?.name ?? '—';
  return (
    <Card
      title={t('rules.title')}
      bodyClass=""
      actions={
        canAny(me, 'schedule.manage') && (
          <button
            type="button"
            className="btn btn-primary"
            disabled={!clientId || !groups.data?.items.length}
            onClick={() => setEditing('new')}
          >
            <Plus size={15} /> {t('rules.new')}
          </button>
        )
      }
    >
      <div className="card-body muted small" style={{ paddingBottom: 0 }}>
        {t('rules.hint')}
      </div>
      <div className="ops-scroll">
        <table className="table table-wrap">
          <thead>
            <tr>
              <th>{t('common.name')}</th>
              <th>{t('rules.when')}</th>
              <th>{t('rules.then')}</th>
              <th>{t('rules.last')}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {(rules.data?.items ?? []).map((r) => (
              <tr key={r.id} style={r.enabled ? undefined : { opacity: 0.55 }}>
                <td>
                  <strong>{r.name}</strong>
                  {!r.enabled && (
                    <span className="pill" style={{ marginLeft: 6 }}>
                      {t('rules.disabled')}
                    </span>
                  )}
                </td>
                <td className="small">
                  {t(`catalog.metric.${r.metric_key}` as 'catalog.metric.electrical.power_w', {
                    defaultValue: r.metric_key,
                  })}{' '}
                  {r.operator} {r.threshold}
                  <span className="cell-sub">
                    {r.source_device_name ?? t('rules.anyOf', { group: gname(r.source_group_id) })}
                  </span>
                </td>
                <td className="small">
                  {gname(r.target_group_id)} → {levelText(r.level, t)}
                  <span className="cell-sub">
                    {t('rules.forMin', { n: r.duration_min, cooldown: r.cooldown_min })}
                  </span>
                </td>
                <td className="small muted">
                  {r.last_fired_at
                    ? `${formatDateTime(r.last_fired_at)} (${r.last_value ?? '—'})`
                    : t('rules.never')}
                  <span className="cell-sub">{t('rules.fired', { n: r.fired_count })}</span>
                </td>
                <td className="right">
                  {r.can_manage && (
                    <button type="button" className="btn btn-ghost" onClick={() => setEditing(r)}>
                      {t('common.edit')}
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {rules.data?.items.length === 0 && (
              <tr>
                <td colSpan={5} className="muted">
                  {t('rules.none')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {editing && clientId && (
        <RuleModal
          clientId={clientId}
          rule={editing === 'new' ? null : editing}
          groups={groups.data?.items ?? []}
          onClose={() => setEditing(null)}
        />
      )}
    </Card>
  );
}

function RuleModal({
  clientId,
  rule,
  groups,
  onClose,
}: {
  clientId: string;
  rule: api.AutomationRule | null;
  groups: api.Group[];
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const catalog = useQuery({
    queryKey: ['metrics', 'catalog'],
    queryFn: fetchMetricCatalog,
    staleTime: 600_000,
  });
  const [search, setSearch] = useState('');
  const devices = useQuery({
    queryKey: ['rule-devices', clientId, search],
    queryFn: async () => (await getDataSource()).listDevices({ siteId: clientId, search, limit: 50 }),
  });
  const [f, setF] = useState({
    name: rule?.name ?? '',
    enabled: rule?.enabled ?? true,
    sourceKind: rule?.source_group_id ? 'group' : 'device',
    source_device_id: rule?.source_device_id ?? '',
    source_group_id: rule?.source_group_id ?? groups[0]?.id ?? '',
    metric_key: rule?.metric_key ?? 'traffic.vehicle_count',
    operator: rule?.operator ?? ('>' as api.AutomationRule['operator']),
    threshold: String(rule?.threshold ?? 30),
    max_age_s: String(rule?.max_age_s ?? 900),
    target_group_id: rule?.target_group_id ?? groups[0]?.id ?? '',
    level: String(rule?.level ?? 100),
    duration_min: String(rule?.duration_min ?? 15),
    cooldown_min: String(rule?.cooldown_min ?? 0),
  });
  const set = (p: Partial<typeof f>) => setF({ ...f, ...p });
  const save = useMutation({
    mutationFn: () =>
      saveRule(rule?.id ?? null, {
        client_id: clientId,
        name: f.name,
        enabled: f.enabled,
        ...(f.sourceKind === 'device'
          ? { source_device_id: f.source_device_id, source_group_id: null }
          : { source_group_id: f.source_group_id, source_device_id: null }),
        metric_key: f.metric_key,
        operator: f.operator,
        threshold: Number(f.threshold),
        max_age_s: Number(f.max_age_s),
        target_group_id: f.target_group_id,
        level: Number(f.level),
        duration_min: Number(f.duration_min),
        cooldown_min: Number(f.cooldown_min),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['automation-rules'] });
      onClose();
    },
  });
  const del = useMutation({
    mutationFn: () => deleteRule(rule!.id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['automation-rules'] });
      onClose();
    },
  });
  const sourceOk = f.sourceKind === 'device' ? !!f.source_device_id : !!f.source_group_id;
  return (
    <Modal title={rule ? rule.name : t('rules.new')} onClose={onClose}>
      <div className="form-grid">
        <Field label={t('common.name')}>
          <input
            className="input"
            value={f.name}
            onChange={(e) => set({ name: e.target.value })}
            maxLength={120}
          />
        </Field>
        <Field label={t('rules.source')}>
          <select
            className="input"
            value={f.sourceKind}
            onChange={(e) => set({ sourceKind: e.target.value })}
          >
            <option value="device">{t('rules.oneDevice')}</option>
            <option value="group">{t('rules.anyDeviceOfGroup')}</option>
          </select>
        </Field>
        {f.sourceKind === 'device' ? (
          <Field label={t('rules.device')}>
            <input
              className="input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('common.search')}
            />
            <select
              className="input"
              value={f.source_device_id}
              onChange={(e) => set({ source_device_id: e.target.value })}
              style={{ marginTop: 4 }}
            >
              <option value="">—</option>
              {rule?.source_device_id && rule.source_device_name && (
                <option value={rule.source_device_id}>{rule.source_device_name}</option>
              )}
              {(devices.data?.items ?? [])
                .filter((d) => d.id !== rule?.source_device_id)
                .map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
            </select>
          </Field>
        ) : (
          <Field label={t('rules.sourceGroup')}>
            <select
              className="input"
              value={f.source_group_id}
              onChange={(e) => set({ source_group_id: e.target.value })}
            >
              {groups.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
            </select>
          </Field>
        )}
        <Field label={t('rules.metric')}>
          <select
            className="input"
            value={f.metric_key}
            onChange={(e) => set({ metric_key: e.target.value })}
          >
            {(catalog.data?.items ?? []).map((m) => (
              <option key={m.key} value={m.key}>
                {t(`catalog.metric.${m.key}` as 'catalog.metric.electrical.power_w', {
                  defaultValue: m.name_en,
                })}{' '}
                ({m.key})
              </option>
            ))}
          </select>
        </Field>
        <Field label={t('rules.condition')}>
          <span className="toolbar">
            <select
              className="input"
              value={f.operator}
              onChange={(e) => set({ operator: e.target.value as typeof f.operator })}
              style={{ width: 70 }}
              aria-label={t('rules.operator')}
            >
              {(['>', '>=', '<', '<=', '='] as const).map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </select>
            <input
              className="input"
              inputMode="decimal"
              value={f.threshold}
              onChange={(e) => set({ threshold: e.target.value })}
              aria-label={t('rules.threshold')}
            />
          </span>
        </Field>
        <Field label={t('rules.maxAge')} hint={t('rules.maxAgeHint')}>
          <input
            className="input"
            type="number"
            min={10}
            max={86400}
            value={f.max_age_s}
            onChange={(e) => set({ max_age_s: e.target.value })}
          />
        </Field>
        <Field label={t('rules.target')}>
          <select
            className="input"
            value={f.target_group_id}
            onChange={(e) => set({ target_group_id: e.target.value })}
          >
            {groups.map((g) => (
              <option key={g.id} value={g.id}>
                {g.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t('groupsPage.level')}>
          <input
            className="input"
            type="number"
            min={0}
            max={100}
            value={f.level}
            onChange={(e) => set({ level: e.target.value })}
          />
        </Field>
        <Field label={t('rules.duration')}>
          <input
            className="input"
            type="number"
            min={1}
            max={1440}
            value={f.duration_min}
            onChange={(e) => set({ duration_min: e.target.value })}
          />
        </Field>
        <Field label={t('rules.cooldown')}>
          <input
            className="input"
            type="number"
            min={0}
            max={1440}
            value={f.cooldown_min}
            onChange={(e) => set({ cooldown_min: e.target.value })}
          />
        </Field>
      </div>
      <label className="check-row">
        <input type="checkbox" checked={f.enabled} onChange={(e) => set({ enabled: e.target.checked })} />{' '}
        {t('rules.enabled')}
      </label>
      {save.isError && <p className="form-error">{t('errors.INVALID_REQUEST')}</p>}
      <div className="form-actions">
        {rule && (
          <button type="button" className="btn btn-danger" onClick={() => del.mutate()}>
            {t('common.delete')}
          </button>
        )}
        <span style={{ flex: 1 }} />
        <button type="button" className="btn" onClick={onClose}>
          {t('common.cancel')}
        </button>
        <button
          type="button"
          className="btn btn-primary"
          disabled={!f.name.trim() || !sourceOk || !f.target_group_id || save.isPending}
          onClick={() => save.mutate()}
        >
          {t('common.save')}
        </button>
      </div>
    </Modal>
  );
}
